// Strict parser for "Full Name: line" dialogue scripts.
// Drops malformed lines; caller fails the convo if <50% of lines parse.
import type { DialogueLine } from '../types.js';

export interface ParseResult {
  lines: DialogueLine[];
  rawCount: number; // non-empty input lines
  dropped: number;
}

export function parseDialogue(
  raw: string,
  nameA: string,
  nameB: string,
): ParseResult {
  const aliases = new Map<string, string>();
  for (const full of [nameA, nameB]) {
    aliases.set(full.toLowerCase(), full);
    const first = full.split(' ')[0]?.toLowerCase();
    if (first) aliases.set(first, full); // tolerate "First: ..." shorthand
  }

  const lines: DialogueLine[] = [];
  let rawCount = 0;
  for (const chunk of raw.split('\n')) {
    const line = chunk.trim();
    if (!line) continue;
    rawCount++;
    const m = line.match(/^(.+?)\s*:\s*(.+)$/);
    if (!m) continue;
    const speaker = aliases.get(m[1].trim().toLowerCase());
    const text = m[2].trim();
    // Drop narration, stage directions, empty lines.
    if (!speaker || !text || /^\[.+\]$/.test(text) || /^\*.+\*$/.test(text)) continue;
    lines.push({ speaker, text: text.slice(0, 280) });
  }
  return { lines, rawCount, dropped: rawCount - lines.length };
}

const OFFLINE_THOUGHTS = [
  'I keep almost saying the thing I actually mean.',
  'There is a song in my head and I cannot place where I heard it.',
  'I dreamed about a door that was not in the house.',
  'I still owe someone an apology I have not figured out how to say.',
  'I was thinking about a kitchen I have not stood in for years.',
];

/** Short local exchange when the model is unavailable, so a meeting still talks. */
export function offlineChatLines(nameA: string, nameB: string, firstA: string, firstB: string): DialogueLine[] {
  const thought = OFFLINE_THOUGHTS[Math.floor(Math.random() * OFFLINE_THOUGHTS.length)] ?? OFFLINE_THOUGHTS[0];
  return [
    { speaker: nameA, text: thought },
    { speaker: nameB, text: 'Where did that come from?' },
    { speaker: nameA, text: `It just showed up, ${firstB}. I was not planning to say it.` },
    { speaker: nameB, text: `Alright, ${firstA}. I am listening.` },
  ];
}

// Conversation is usable if at least half the raw lines parsed
// and both speakers appear at least once.
export function isUsableScript(r: ParseResult): boolean {
  if (r.rawCount === 0 || r.lines.length === 0) return false;
  if (r.lines.length < r.rawCount / 2) return false;
  const speakers = new Set(r.lines.map((l) => l.speaker));
  return speakers.size >= 2;
}

export function randomLengthClass(): 'short' | 'medium' | 'long' {
  const r = Math.random();
  if (r < 0.45) return 'short';
  if (r < 0.8) return 'medium';
  return 'long';
}
